import React from 'react';
import { Typography, TypographyProps } from '@mui/material';

/**
 * Renders a company name with a grey ".ai" suffix when present (e.g. PROVYSN.ai, Provysn.ai).
 * For custom names without ".ai", shows the full name in the primary style.
 */
export const BrandedWordmark: React.FC<{
  name: string;
  variant?: TypographyProps['variant'];
  /** onPrimary: light text on colored app bars; default: dark text on light surfaces */
  tone?: 'onPrimary' | 'default';
  /** Base (non-.ai) part typography */
  baseSx?: TypographyProps['sx'];
  /** ".ai" suffix overrides */
  suffixSx?: TypographyProps['sx'];
  /** Combined sx applied to the outer Typography */
  sx?: TypographyProps['sx'];
  component?: TypographyProps['component'];
}> = ({
  name,
  variant = 'h6',
  tone = 'default',
  baseSx,
  suffixSx,
  sx,
  component = 'span',
}) => {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return null;
  }

  const lower = trimmed.toLowerCase();
  const aiSuffix = '.ai';
  const suffixColor =
    tone === 'onPrimary' ? 'rgba(255,255,255,0.72)' : 'text.secondary';

  if (lower.endsWith(aiSuffix)) {
    const base = trimmed.slice(0, trimmed.length - aiSuffix.length);
    return (
      <Typography variant={variant} component={component} sx={{ display: 'inline-flex', alignItems: 'baseline', ...sx }}>
        <Typography component="span" variant={variant} sx={{ fontWeight: 'inherit', ...baseSx }}>
          {base}
        </Typography>
        <Typography
          component="span"
          variant={variant}
          sx={{
            fontWeight: 'inherit',
            color: suffixColor,
            ...suffixSx,
          }}
        >
          {aiSuffix}
        </Typography>
      </Typography>
    );
  }

  return (
    <Typography variant={variant} component={component} sx={sx}>
      {trimmed}
    </Typography>
  );
};

export default BrandedWordmark;
